import type { MarketData, InvestmentRange, MarketBenchmark } from '@/types';
import marketDataJson from '../../../data/dados_mercado.json';

let cachedData: MarketData | null = null;

export function loadMarketData(): MarketData {
  if (cachedData) {
    return cachedData;
  }

  cachedData = marketDataJson as MarketData;
  return cachedData;
}

export function getInvestmentRange(investmentValue: number): InvestmentRange | null {
  const data = loadMarketData();

  for (const range of data.faixas_investimento) {
    if (investmentValue >= range.min) {
      if (range.max === null || investmentValue < range.max) {
        return range;
      }
    }
  }

  return null;
}

export function getBenchmark(
  investmentRangeId: string,
  segmento: string
): MarketBenchmark | null {
  const data = loadMarketData();

  const benchmark = data.dados_mercado.find(
    d => d.faixa_investimento === investmentRangeId && d.segmento === segmento
  );

  if (benchmark) {
    return benchmark;
  }

  const fallback = data.dados_mercado.find(
    d => d.faixa_investimento === investmentRangeId
  );

  return fallback || null;
}

export function getAverageBenchmark(segmento: string): MarketBenchmark | null {
  const data = loadMarketData();

  const segmentBenchmarks = data.dados_mercado.filter(d => d.segmento === segmento);

  if (segmentBenchmarks.length === 0) {
    return null;
  }

  const avg: MarketBenchmark = {
    faixa_investimento: 'media',
    segmento,
    payback_meses: Math.round(
      segmentBenchmarks.reduce((sum, b) => sum + b.payback_meses, 0) /
        segmentBenchmarks.length
    ),
    faturamento_medio_mensal: Math.round(
      segmentBenchmarks.reduce((sum, b) => sum + b.faturamento_medio_mensal, 0) /
        segmentBenchmarks.length
    ),
    taxa_franquia: Math.round(
      segmentBenchmarks.reduce((sum, b) => sum + b.taxa_franquia, 0) /
        segmentBenchmarks.length
    ),
    royalties:
      segmentBenchmarks.reduce((sum, b) => sum + b.royalties, 0) /
      segmentBenchmarks.length,
    vendas_mensais: Math.round(
      segmentBenchmarks.reduce((sum, b) => sum + b.vendas_mensais, 0) /
        segmentBenchmarks.length
    )
  };

  return avg;
}

export function getAllSegments(): string[] {
  const data = loadMarketData();
  return data.segmentos;
}

export function getAllInvestmentRanges(): InvestmentRange[] {
  const data = loadMarketData();
  return data.faixas_investimento;
}
